import { useState, useEffect } from 'react';

// Reverse-geocodes (lat, lon) → { city, state } via OpenStreetMap Nominatim.
// Shared by the events tooltip and the drone location-history modal.
//
// Caching strategy:
//   1. In-memory Map (cleared on page reload) — fastest sync read.
//   2. localStorage (persists across sessions) — populated on first cold fetch.
//   3. In-flight Map — dedupes concurrent calls for the same key.
//
// Cache key is coords rounded to 2 decimals (~1km). Two events in the same city
// almost always collapse to one cache entry, which keeps Nominatim happy
// (1 req/sec limit) and makes hovering 50 events feel instant after warm-up.

const STORAGE_KEY = 'firefleet:geocode-cache:v1';
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/reverse';
const RATE_LIMIT_MS = 1100; // Nominatim asks for ≤1 req/sec; leave headroom.

const memCache = new Map();
const inflight = new Map();
const taskStack = []; // LIFO — newest hover jumps to the front
let pumping = false;
let storageLoaded = false;
let lastRequestAt = 0;

const cacheKey = (lat, lon) => `${Number(lat).toFixed(2)},${Number(lon).toFixed(2)}`;

function loadStorage() {
  if (storageLoaded) return;
  storageLoaded = true;
  try {
    const obj = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    for (const [k, v] of Object.entries(obj)) memCache.set(k, v);
  } catch {
    // Quota / parse errors — proceed with empty cache.
  }
}

function persistStorage() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(memCache)));
  } catch {
    // Storage full or disabled — ignore; in-memory cache still works for the session.
  }
}

// Synchronous read — returns the cached value (object or null) or `undefined`
// if we've never looked up these coords.
export function getCachedCityState(lat, lon) {
  if (lat == null || lon == null) return undefined;
  loadStorage();
  return memCache.get(cacheKey(lat, lon));
}

// Single shared pump that drains the LIFO task stack with the rate limit applied centrally.
// Idle when stack is empty; restarts on next enqueue.
function pump() {
  if (pumping) return;
  pumping = true;
  (async () => {
    try {
      while (taskStack.length > 0) {
        const task = taskStack.pop();
        const wait = Math.max(0, RATE_LIMIT_MS - (Date.now() - lastRequestAt));
        if (wait > 0) await new Promise(r => setTimeout(r, wait));
        lastRequestAt = Date.now();
        await task();
      }
    } finally {
      pumping = false;
    }
  })();
}

// Async fetch with cache + dedup + LIFO-prioritized throttle. Returns { city, state } or null.
// Most-recently-enqueued requests are processed first, so a fresh hover jumps ahead of stale ones.
export async function geocodeCityState(lat, lon) {
  if (lat == null || lon == null) return null;
  loadStorage();
  const key = cacheKey(lat, lon);
  if (memCache.has(key)) return memCache.get(key);
  if (inflight.has(key)) return inflight.get(key);

  let resolveOuter;
  const promise = new Promise(res => { resolveOuter = res; });
  inflight.set(key, promise);

  const task = async () => {
    try {
      const url = `${NOMINATIM_URL}?format=json&lat=${lat}&lon=${lon}&zoom=10`;
      const res = await fetch(url, { headers: { 'User-Agent': 'FirefleetDroneManagement/1.0' } });
      const data = await res.json();
      const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county || null;
      const state = data.address?.state || data.address?.province || null;
      const result = (city || state) ? { city, state } : null;
      memCache.set(key, result);
      persistStorage();
      resolveOuter(result);
    } catch {
      resolveOuter(null);
    } finally {
      inflight.delete(key);
    }
  };

  taskStack.push(task);
  pump();
  return promise;
}

// Convenience formatter — turns a result into a "City, State" string.
export function formatCityState(result) {
  if (!result) return null;
  if (result.city && result.state) return `${result.city}, ${result.state}`;
  return result.city || result.state || null;
}

// React hook: lazily fetches city/state for the given coords.
// Pass `enabled=false` to defer fetching (e.g. until the marker is hovered).
// Returns { city, state } | null | undefined (undefined = not yet known).
//
// Reads the cache directly during render rather than mirroring into useState — useState's
// initializer only runs once, so coord changes between renders would otherwise serve a stale
// value until the effect fired. forceRender just nudges a re-read after a cold fetch lands.
export function useGeocodeCityState(lat, lon, enabled = true) {
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!enabled || lat == null || lon == null) return;
    if (getCachedCityState(lat, lon) !== undefined) return; // already cached — render reads it directly
    let cancelled = false;
    geocodeCityState(lat, lon).then(() => {
      if (!cancelled) forceRender(n => n + 1);
    });
    return () => { cancelled = true; };
  }, [lat, lon, enabled]);

  return getCachedCityState(lat, lon);
}
